import Map, { Layer, Source } from 'react-map-gl';
import PropTypes from 'prop-types';
import { memo, useRef, useEffect, useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import bbox from '@turf/bbox';
import { Backdrop, CircularProgress } from '@mui/material';
import { MapControl, StyledControlPanel, MapPopup } from '../../map';
import FrontEndMainPyramidSelector from '../FrontEndMainPyramidSelector';
import { useSelector } from '../../../redux/store';
import { isNilOrBlank } from '../../../utils/core';
import GeoMapPopUp from '../GeoMapPopUp';

// ----------------------------------------------------------------------

function MapViewportAnimation({ ...other }) {
  const theme = useTheme();
  const mapRef = useRef(null);
  const { mapFeatures, currentEntity, boundingEntity, mapIsFetching } = useSelector((state) => state.frontend);
  const [hoverInfo, setHoverInfo] = useState(null);
  const handleMouseMove = useCallback((event) => {
    const { features } = event;
    if (features && features.length > 0) {
      setHoverInfo({
        feature: features[0],
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat
      });
    } else {
      setHoverInfo(null); // Clear hover info when not hovering over features
    }
  }, []);

  const pointLayer = {
    id: 'point',
    type: 'circle',
    paint: {
      'circle-radius': [
        'case',
        ['==', ['get', 'status'], 'active_leaf'], // Check if status equals 'active_leaf'
        8, //  if true
        6 //  if false
      ],
      'circle-opacity': 0.8,
      'circle-stroke-color': '#000000',
      'circle-stroke-width': ['case', ['==', ['get', 'status'], 'active_leaf'], 3, 1],
      'circle-color': [
        'case',
        ['!=', ['get', 'color', ['get', 'group', ['properties']]], null],
        ['get', 'color', ['get', 'group', ['properties']]],
        theme.palette.primary.main // Default color
      ]
    }
  };

  const lineLayer = {
    id: 'outline',
    type: 'line',
    paint: {
      'line-width': 0.8,
      'line-color': theme.palette.primary.main,
      'line-dasharray': [5, 3]
    }
  };

  const boundingLineLayer = {
    id: 'bounding-line',
    type: 'line',
    paint: {
      'line-width': 1.6,
      'line-color': theme.palette.primary.main
    }
  };

  const boundingFillLayer = {
    id: 'bounding-fill',
    type: 'fill',
    paint: {
      'fill-color': theme.palette.primary.main,
      'fill-opacity': 0.02
    }
  };

  useEffect(() => {
    if (!isNilOrBlank(boundingEntity) && !isNilOrBlank(boundingEntity?.coordinates)) {
      const [minLng, minLat, maxLng, maxLat] = bbox({
        type: 'Feature',
        geometry: boundingEntity?.coordinates
      });
      mapRef.current?.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        { padding: { top: 50, bottom: 50, left: 5, right: 280 }, duration: 1000 }
      );
    }
  }, [currentEntity]); // eslint-disable-line

  return (
    !isNilOrBlank(boundingEntity) && (
      <Map
        initialViewState={{
          bearing: 0,
          pitch: 0
        }}
        onMouseMove={handleMouseMove} // Detect mouse movements
        interactiveLayerIds={['point', 'outline']} // Ensure hover works only on this layer
        ref={mapRef}
        {...other}
      >
        <MapControl />
        {mapFeatures && (
          <>
            <Source
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: [
                  {
                    type: 'Feature',
                    geometry: boundingEntity?.coordinates
                  }
                ]
              }}
            >
              <Layer {...boundingFillLayer} />
              <Layer {...boundingLineLayer} />
            </Source>
            <Source
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: mapFeatures?.features.filter(
                  (f) => f.geometry.type !== 'Point' && f.properties.id !== boundingEntity.id
                )
              }}
            >
              <Layer {...lineLayer} />
            </Source>
            <Source
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: mapFeatures?.features.filter((f) => f.geometry.type === 'Point')
              }}
            >
              <Layer {...pointLayer} />
            </Source>
          </>
        )}
        {!isNilOrBlank(hoverInfo) && (
          <MapPopup latitude={hoverInfo.latitude} longitude={hoverInfo.longitude} onClose={() => {}}>
            <GeoMapPopUp hoverInfo={hoverInfo} />
          </MapPopup>
        )}

        <Backdrop
          invisible
          sx={{ position: 'absolute', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={mapIsFetching}
        >
          <CircularProgress size={32} />
        </Backdrop>
        <StyledControlPanel>
          <FrontEndMainPyramidSelector textColor="common.white" />
        </StyledControlPanel>
      </Map>
    )
  );
}

MapViewportAnimation.propTypes = {
  data: PropTypes.array
};

export default memo(MapViewportAnimation);
